import {
  VNextButton,
  VNextDialog,
  VNextField,
  VNextInput,
  VNextNotice,
  VNextSelect,
} from "@/vnext/primitives";
import type {
  DemoAccessIdentifierType,
  DemoAccessRevokeTarget,
} from "@/vnext/routes/settings/demo-access/demoAccessRouteModel";

interface DemoAccessDialogsProps {
  grantDialogOpen: boolean;
  identifierType: DemoAccessIdentifierType;
  isGranting: boolean;
  isRevoking: boolean;
  onCloseGrantDialog: () => void;
  onCloseRevokeDialog: () => void;
  onConfirmGrant: () => void;
  onConfirmRevoke: () => void;
  onIdentifierTypeChange: (identifierType: DemoAccessIdentifierType) => void;
  onUserIdentifierChange: (value: string) => void;
  revokeTarget: DemoAccessRevokeTarget | null;
  userIdentifier: string;
}

export function DemoAccessDialogs({
  grantDialogOpen,
  identifierType,
  isGranting,
  isRevoking,
  onCloseGrantDialog,
  onCloseRevokeDialog,
  onConfirmGrant,
  onConfirmRevoke,
  onIdentifierTypeChange,
  onUserIdentifierChange,
  revokeTarget,
  userIdentifier,
}: DemoAccessDialogsProps) {
  return (
    <>
      <VNextDialog
        description="Grant demo-company access to one person at a time. Use Firebase UID when you have it. Use email only as a lookup."
        footer={
          <>
            <VNextButton onClick={onCloseGrantDialog} tone="quiet">
              Cancel
            </VNextButton>
            <VNextButton
              disabled={userIdentifier.trim().length === 0 || isGranting}
              onClick={onConfirmGrant}
              tone="primary"
            >
              {isGranting ? "Granting…" : "Grant access"}
            </VNextButton>
          </>
        }
        onClose={onCloseGrantDialog}
        open={grantDialogOpen}
        title="Grant demo access"
      >
        <div className="demo-access-route__dialog-body">
          <VNextNotice tone="warning">
            Grant access only for internal demos or training, then revoke it
            once that work finishes.
          </VNextNotice>

          <VNextField
            hint="Use Firebase UID for the safest match. Use email only when you do not have the UID yet."
            label="Identifier type"
          >
            <VNextSelect
              aria-label="Identifier type"
              onChange={(event) =>
                onIdentifierTypeChange(
                  event.target.value as DemoAccessIdentifierType,
                )
              }
              value={identifierType}
            >
              <option value="email">Email address</option>
              <option value="firebaseId">Firebase UID</option>
            </VNextSelect>
          </VNextField>

          <VNextField
            hint={
              identifierType === "email"
                ? "Example: user@example.com"
                : "Example: firebase-uid-123"
            }
            label={
              identifierType === "email" ? "Email address" : "Firebase UID"
            }
          >
            <VNextInput
              aria-label={
                identifierType === "email" ? "Email address" : "Firebase UID"
              }
              onChange={(event) => onUserIdentifierChange(event.target.value)}
              placeholder={
                identifierType === "email"
                  ? "user@example.com"
                  : "firebase-uid-123"
              }
              value={userIdentifier}
            />
          </VNextField>
        </div>
      </VNextDialog>

      <VNextDialog
        description={
          revokeTarget
            ? `Remove demo-company access for ${revokeTarget.displayName} now.`
            : "Remove demo-company access for the selected user."
        }
        footer={
          <>
            <VNextButton
              disabled={isRevoking}
              onClick={onCloseRevokeDialog}
              tone="quiet"
            >
              Cancel
            </VNextButton>
            <VNextButton
              disabled={isRevoking || revokeTarget === null}
              onClick={onConfirmRevoke}
              tone="secondary"
            >
              {isRevoking ? "Revoking…" : "Revoke access"}
            </VNextButton>
          </>
        }
        onClose={onCloseRevokeDialog}
        open={revokeTarget !== null}
        title="Revoke demo access"
      >
        {revokeTarget ? (
          <div className="demo-access-route__dialog-body">
            <VNextNotice tone="warning">
              This removes the selected user’s demo-company access.
            </VNextNotice>
            <p className="demo-access-route__dialog-copy">
              {revokeTarget.displayName}
            </p>
            <p className="demo-access-route__dialog-copy demo-access-route__dialog-copy--muted">
              Firebase UID: {revokeTarget.firebaseId}
            </p>
          </div>
        ) : null}
      </VNextDialog>
    </>
  );
}
